import React, { type FC, useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { Script } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { useI18next } from "gatsby-plugin-react-i18next";

import Header from "@components/header";
import LiveChatPopup from "@components/live_chat_popup";
import CookieType from "@constants/CookieType";
import {
  useAccountDetailState,
  useAccountDetailStore,
} from "@stores/account_detail";
import {
  useAuthenticationState,
  useAuthenticationStore,
} from "@stores/authentication";
import {
  useConfigurationState,
  useConfigurationStore,
} from "@stores/configuration";
import axios from "@utilities/axios";
import { loadCurrencies } from "@utilities/currencies";
import { isDebugMode, isProductionMode } from "@utilities/helper";
import Cookies from "js-cookie";

const Layout: FC<{ showHeader?: boolean; children?: any }> = ({
  showHeader,
  children,
}) => {
  const { path, language, changeLanguage } = useI18next();

  const [isHydrated, setIsHydrated] = useState<boolean>(false);

  const authenticationState = useAuthenticationState();
  const accountDetailState = useAccountDetailState();
  const configurationState = useConfigurationState();

  const authPaths = [
    "login",
    "register",
    "forgot-password",
    "new-change-password",
  ];
  const isAuthPage = authPaths.some(authPath => path.includes(authPath));
  const isGeneralLayout = showHeader ?? !isAuthPage;

  const initializeData = async () => {
    await loadCurrencies(language);
    const tokenFromCookie = Cookies.get(CookieType.TOKEN) ?? "";
    if (isProductionMode()) {
      if (authenticationState.getToken() !== tokenFromCookie) {
        if (tokenFromCookie) {
          authenticationState.setToken(tokenFromCookie);
          accountDetailState.load(tokenFromCookie);
        } else {
          authenticationState.clear();
          accountDetailState.clear();
        }
        if (isDebugMode()) {
          console.log("loaded token from cookie 🍪.");
        }
      } else if (authenticationState.isAuthenticated()) {
        accountDetailState.load(authenticationState.getToken());
      }
    }

    const currencyFromCookie = Cookies.get(CookieType.CURRENCY) ?? "";
    if (isProductionMode()) {
      if (configurationState.currency !== currencyFromCookie) {
        configurationState.updateCurrency(
          currencyFromCookie ?? process.env.GATSBY_DEFAULT_CURRENCY,
        );
        if (isDebugMode()) {
          console.log("loaded currency token from cookie 🍪.");
        }
      }
    }

    if (typeof window !== "undefined" && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      configurationState.updateAffiliate(params.get("a") ?? "site");
      if (isDebugMode()) {
        console.log("registered affiliate to cookie 🍪.");
      }

      configurationState.updateCouponCode(params.get("coupon-code") ?? "");
      if (isDebugMode()) {
        console.log("registered coupon code.");
      }

      const currencyParam = params.get("currency");
      if (currencyParam) {
        configurationState.updateCurrency(currencyParam.toUpperCase());
        if (isDebugMode()) {
          console.log("updated currency.");
        }
      }

      const langFromCookie = Cookies.get(CookieType.LANG) ?? "";
      let isLanguageChanged: boolean = false;
      if (!params.get("product")) {
        if (authenticationState.isAuthenticated()) {
          const formData = new FormData();
          formData.append("action", "local-info-only");
          const response = await axios.post("/my_account_details/", formData);
          const savedLanguage = response.data?.language ?? "";
          if (!!savedLanguage && savedLanguage !== "en") {
            changeLanguage(langFromCookie);
            isLanguageChanged = true;
          }
        }
        if (!isLanguageChanged) {
          if (isProductionMode()) {
            if (language !== langFromCookie) {
              if (langFromCookie) {
                changeLanguage(langFromCookie);
                if (isDebugMode()) {
                  console.log("loaded language token from cookie 🍪.");
                }
              }
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    if (isHydrated) {
      initializeData();
    } else {
      useAccountDetailStore.persist.rehydrate();
      useAuthenticationStore.persist.rehydrate();
      useConfigurationStore.persist.rehydrate();
      setIsHydrated(true);
    }
  }, [isHydrated]);

  return (
    <div className="contents selection:bg-primary/80 selection:text-white">
      <Script src="/js/trackers.js" />
      {isGeneralLayout ? (
        <>
          <Header />
          <div className="pt-24">
            <main>{children}</main>
          </div>
        </>
      ) : (
        <div className="container flex h-dvh flex-col items-center justify-center gap-3 py-4 sm:gap-4 md:gap-6 md:py-5 lg:flex-row lg:justify-between lg:gap-0 lg:px-6">
          <div className="flex w-full items-center justify-center lg:w-auto lg:max-w-[320px]">
            <StaticImage
              className="size-full w-[60vw] lg:w-full"
              src="../assets/images/bnesim_logo.svg"
              loading="eager"
              alt="Official BNESIM Logo – A sleek modern design representing the world’s best travel eSIM provider for global connectivity and seamless communication."
              placeholder="none"
            />
          </div>
          <main className="w-full lg:max-w-[calc(100%-344px)]">{children}</main>
        </div>
      )}
      <ToastContainer className="text-sm" draggable />
      <LiveChatPopup />
    </div>
  );
};

export default Layout;
